import Link from 'next/link'
import { useRouter } from 'next/router'

import { Disclosure } from '@headlessui/react'
import { cx } from '@linaria/core'

import { useFoodSWR, useFoodSWRInfinite } from '~/api-client/food_api'
import { userDinnerParams } from '~/api-client/food_api/user'
import { BackButton } from '~/components/action/BackButton'
import { AppImage } from '~/components/data-display/AppImage'
import { DinnersDisclosure } from '~/components/food_app/dinner/DinnersDisclosure'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { userProfileIcon } from '~/libs/user'
import { useAccessToken } from '~/provider/SessionProvider'

const Page = () => {
  const accessToken = useAccessToken({ loginRequired: true })

  // かならず認証付きで指定するため userId の指定は必要ない代わりに、
  // accessToken なしではリクエストしないようにする必要がある

  const userOrganizingDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.organizing(),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      disabled: accessToken == null,
    }
  )

  const userAttendingDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.attending(),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      disabled: accessToken == null,
    }
  )

  const userAppliedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.applied(),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      disabled: accessToken == null,
    }
  )

  const userInvitedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.invited(),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      disabled: accessToken == null,
    }
  )

  const userOrganizedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.organized(),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      disabled: accessToken == null,
    }
  )

  const userAttendedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.attended(),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      disabled: accessToken == null,
    }
  )

  const attendeesRes = useFoodSWR('/user/dinner_attendees', {})

  const router = useRouter()
  const { default_disclosure } = router.query

  return (
    <div>
      <Header>
        <div className="w-5">
          <BackButton />
        </div>
        食事会リスト
        <div className="w-5" />
      </Header>
      <Container>
        <ContainerInset>
          <form>
            {/* defaultOpen は初回 render の値が使われるため isReady を待ってから描画する必要がある */}
            {router.isReady && (
              <div className="mt-4">
                <DinnersDisclosure
                  showParticipationStatus
                  titleText="幹事の食事会"
                  noContentText="幹事の食事会はありません"
                  paginatedDinners={userOrganizingDinners}
                  defaultOpen={default_disclosure === 'organizing'}
                />
                <DinnersDisclosure
                  showParticipationStatus
                  titleText="参加予定の食事会"
                  noContentText="参加予定の食事会はありません"
                  paginatedDinners={userAttendingDinners}
                  defaultOpen={default_disclosure === 'attending'}
                />
                <DinnersDisclosure
                  showParticipationStatus
                  titleText="応募中の食事会"
                  noContentText="応募中の食事会はありません"
                  paginatedDinners={userAppliedDinners}
                  defaultOpen={default_disclosure === 'applied'}
                />
                <DinnersDisclosure
                  showParticipationStatus
                  titleText="誘われ中の食事会"
                  noContentText="誘われ中の食事会はありません"
                  paginatedDinners={userInvitedDinners}
                  defaultOpen={default_disclosure === 'invited'}
                />
                <DinnersDisclosure
                  showParticipationStatus
                  titleText="過去に幹事をした食事会"
                  noContentText="過去に幹事をした食事会はありません"
                  paginatedDinners={userOrganizedDinners}
                  defaultOpen={default_disclosure === 'organized'}
                />
                <DinnersDisclosure
                  showParticipationStatus
                  titleText="過去に参加した食事会"
                  noContentText="過去に参加した食事会はありません"
                  paginatedDinners={userAttendedDinners}
                  defaultOpen={default_disclosure === 'attended'}
                />

                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button
                        className={cx(
                          'relative flex w-full justify-between rounded-lg bg-[#E6E6EA] px-4 py-3 text-left text-sm font-medium',
                          !open && 'mb-4'
                        )}
                      >
                        <span>同席者一覧</span>

                        <div
                          className={cx(
                            `absolute top-5 right-4 w-2 h-2 border-t border-t-black border-l`,
                            open && 'rotate-[45deg]',
                            !open && 'rotate-[225deg]'
                          )}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="py-4 text-sm text-gray-500">
                        <div className="text-right pb-4">同席回数</div>
                        {attendeesRes.data?.attendees.map((attendee, i) => {
                          return (
                            <div key={attendee.user.userId}>
                              <div className="flex">
                                <Link
                                  href={`/foodies/${attendee.user.userName}`}
                                >
                                  <div className="flex-none">
                                    <AppImage
                                      variant={{
                                        type: 'profile',
                                        user: attendee.user,
                                      }}
                                      rounded="full"
                                      width={50}
                                      height={50}
                                      className="rounded-full"
                                      alt={`${attendee.user.userId}`}
                                      src={userProfileIcon(attendee.user)}
                                    />
                                  </div>
                                </Link>
                                <div className="flex-1 pl-2">
                                  <div className="flex flex-col">
                                    <div className="font-bold text-sm pt-1">
                                      {attendee.user.displayName}
                                    </div>
                                    <div className="whitespace-pre-wrap break-words text-[13px] text-[#6C6C6C]">
                                      @{attendee.user.userName}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between pr-1">
                                  <div className="text-[18px]">
                                    {attendee.attendedCount}
                                  </div>
                                  <div className="text-[13px] pl-1">回</div>
                                </div>
                              </div>

                              <div
                                className={cx(
                                  'w-full my-5',
                                  attendeesRes.data?.attendees.length !==
                                    i - 1 &&
                                    'border border-dashed border-[#CCCCCC]'
                                )}
                              />
                            </div>
                          )
                        })}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            )}
          </form>
        </ContainerInset>
      </Container>
      <Footer />
    </div>
  )
}

export default Page
